import { isEmpty, isNil } from 'lodash';
import { API } from '@shared/types/api';
import { ApiFilterByComponent } from '../types';
import { getRelatedEntityPathKey } from './relatedEntityPath';

// The recursive shape `matchesRelatedEntity` nests in. The generated fragment type spells each
// level out separately (GraphQL fragments can't recurse), so walking it needs a type that can.
// The index signature carries the one-of fields this walk doesn't read; without it the deepest
// level, which has no `matchesRelatedEntity` to match on, is rejected as a weak type.
interface NestableFilterOneOf {
  [field: string]: unknown;
  matchesRelatedEntity?: { filterByComponent: NestableFilterByComponent } | null;
}

interface NestableFilterByComponent {
  componentId: string;
  value: NestableFilterOneOf;
}

interface UnwrappedRelatedEntityFilter {
  /** The `RELATED_CARD` component ids the filter travels through, outermost first. */
  componentIds: string[];
  /** The filter at the end of the path - the one that names a field and a value. */
  leaf: ApiFilterByComponent;
  /** True when the path runs deeper than the fragment can read, so `leaf` isn't the real filter. */
  truncated: boolean;
}

/**
 * Splits a saved filter into the related-card hops it travels and the filter at the end of them.
 * A filter on this workflow unwraps to no hops and itself.
 *
 * `leaf` is typed as the outer fragment because every level selects the same one-of fields; the
 * deepest level simply has no `matchesRelatedEntity`, which is what ended the walk.
 */
export function unwrapRelatedEntityFilter(
  filter: ApiFilterByComponent,
): UnwrappedRelatedEntityFilter {
  const componentIds: string[] = [];
  let current: NestableFilterByComponent = filter;
  while (!isNil(current.value.matchesRelatedEntity)) {
    componentIds.push(current.componentId);
    current = current.value.matchesRelatedEntity.filterByComponent;
  }
  return {
    componentIds,
    leaf: current as ApiFilterByComponent,
    truncated: !isEmpty(componentIds) && isEmptyFilterOneOf(current.value),
  };
}

/**
 * Whether every one-of field of a nested level came back empty, which means the walk ran out of
 * fragment rather than reaching the filter.
 *
 * The walk ends at the level with no `matchesRelatedEntity`. For a filter that nests deeper than
 * the fragment spells out, that level is a related-card hop whose own `matchesRelatedEntity` was
 * never selected, so every field on it reads as null. A filter that really ended there would name
 * a value, so all-empty is the signature of truncation. Only meaningful below the top level: a
 * filter on this workflow is what it is, empty value or not.
 */
function isEmptyFilterOneOf(value: NestableFilterOneOf): boolean {
  return Object.entries(value).every(([field, entry]) => field === '__typename' || isNil(entry));
}

/**
 * The `RELATED_CARD` paths every cross-workflow (`matchesRelatedEntity`) filter in this criteria
 * travels, across both top-level filters and groups. Each path is a list of component ids,
 * outermost first; a filter that hops twice contributes one two-id path.
 *
 * Saved criteria only carries component ids, so a nested filter can't be decoded until the related
 * workflow templates it points at have loaded. This tells `ControlsProvider` which paths to
 * resolve, without having to speculatively load a template per related-card component.
 */
export function getRelatedEntityFilterPaths(
  criteria: API.CollectionCriteriaFragment | null | undefined,
): string[][] {
  if (isNil(criteria)) {
    return [];
  }
  const groupFilters = (criteria.groups ?? []).flatMap(group => group.filterByComponents ?? []);
  const paths = [...(criteria.filterByComponents ?? []), ...groupFilters]
    .map(filter => unwrapRelatedEntityFilter(filter).componentIds)
    .filter(componentIds => !isEmpty(componentIds));
  return Array.from(new Map(paths.map(path => [getRelatedEntityPathKey(path), path])).values());
}
